import { useMemo } from 'react';
import { useQuery } from '@common-fe/common-fe';
import { AchievementTrackerItem, costFormater } from '@common-fe/common-fe';
import dayjs from 'dayjs';

import { api } from '@/api';
import { API_URL } from '@/dtos';

import { IrsMaximumsCoverageTypes } from '../types';

export type AchievementTrackerInfoItem = AchievementTrackerItem & {
  maxInitialValue: number;
};



export enum ContributionAchievementsName {
  INDIVIDUAL = 'Individual Max',
  FAMILY = 'Family Max',
  CATCH_UP = 'Catch Up',
}

const contributionAchievementsNameFormatter = (name: IrsMaximumsCoverageTypes) => {
  switch (name) {
    case IrsMaximumsCoverageTypes.INDIVIDUAL:
      return ContributionAchievementsName.INDIVIDUAL;
    case IrsMaximumsCoverageTypes.FAMILY:
      return ContributionAchievementsName.FAMILY;
    default:
      return '';
  }
};

const HSA_KEY = 'HSA_MAX';
const ROLLOVER_KEY = 'ROLLOVER';

const QUERY_KEY = 'getContributionsHistoryQueryKey';

export const useGetContributionsHistoryQuery = (
  employeeId: string,
  personId: string,
  taxYear?: string,
  accountId?: string
) => {
  const params = useMemo(() => {
    const queryGenerator = new URLSearchParams();

    queryGenerator.set('employee_id', employeeId);
    queryGenerator.set('person_id', personId);

    if (taxYear) queryGenerator.set('tax_year', taxYear);
    if (accountId) queryGenerator.set('account_id', `${accountId}`);

    return queryGenerator;
  }, [accountId, employeeId, taxYear, personId]);

  const { isLoading, isSuccess, isError, data, refetch } = useQuery(
    [QUERY_KEY, accountId, taxYear, personId],
    accountId && taxYear && personId
      ? () => api.get(API_URL.MONITOR_CONTRIBUTION_INFO, { params })
      : () => null
  );

  const contributionAchievements = useMemo(() => {
    const filteredItems = data?.data?.irs_limits
      ?.filter((item) => item.type === HSA_KEY)
      ?.filter((item) => item.irs_coverage_level_type !== ROLLOVER_KEY);

    const individualAmount =
      filteredItems?.find(
        (item) =>
          (item.irs_coverage_level_type as unknown as IrsMaximumsCoverageTypes) ===
          IrsMaximumsCoverageTypes.INDIVIDUAL
      )?.amount || 0;

    return filteredItems
      ?.map((item) => ({
        id: `${item.id}`,
        name: contributionAchievementsNameFormatter(
          item.irs_coverage_level_type as unknown as IrsMaximumsCoverageTypes
        ),
        maxValue:
          (item.irs_coverage_level_type as unknown as IrsMaximumsCoverageTypes) ===
          IrsMaximumsCoverageTypes.FAMILY
            ? (item.amount || 0) - individualAmount
            : item.amount,
        maxInitialValue: item.amount,
        maxValueDescription: costFormater(item.amount, true),
      }))
      .sort((a) => (a.name === ContributionAchievementsName.INDIVIDUAL ? -1 : 1));
  }, [data]);

  const formattedData = useMemo(
    () => ({
      items: data?.data?.irs_limits,
      currentValue: data?.data?.total_employee_contribution?.total_contribution_amount || 0,
      isWithCatchUp: data?.data?.is_with_catch_up_age,
    }),
    [data]
  );

  const lastDayForContribution = useMemo(() => {
    if (!data?.data) return undefined;

    return data?.data?.irs_limits?.find((item) => item?.tax_year === dayjs().year() - 1)?.tax_year_end;
  }, [data]);

  return {
    data: data?.data,
    refetch,
    formattedData,
    contributionAchievements: (contributionAchievements as AchievementTrackerInfoItem[]) || [],
    lastDayForContribution,
    isError,
    isLoading,
    isSuccess,
  };
};
